import { aliasStatus, collectAliasUsages, sameRef, } from './answer-ref-alias.js';
import { parseDriverAddress } from './driven-versions.js';
/**
 * WHAT DEPENDS ON A BLOCK, AND WHICH OF IT IS LOAD-BEARING.
 *
 * One question with three callers: the delete guard, which must refuse; the issue list, which
 * must warn; and the publication gate, which must hold. Asked three times it would be answered
 * three ways, and this feature has already produced that split twice.
 *
 * ## Not every reference is worth protecting, and that is the owner's call
 *
 * The rule, in their words: *"if there are 10 chips that reference a block, it's a pain to
 * delete all of them before deleting this block. better to allow delete and have the chips go
 * to error state."* So prose breaks loudly and visibly and nobody is stopped. What cannot be
 * allowed to break is the thing that then decides something silently:
 *
 *   - **driven** — the dependent holds N authored questions and loses the only thing that can
 *     choose between them. Nothing on screen is wrong; it simply asks the wrong one.
 *   - **branch condition** — a condition that cannot resolve decides nothing, and the block
 *     fails closed to a branch the teacher never authored.
 *
 * Those two are refused. An **equation** is allowed, because it fails where the teacher is
 * looking — the key is visibly unset and the block says so — but it is reported, because an
 * equation has no fallback where prose does.
 *
 * ## Why the branch condition is found by key and not by path
 *
 * `AliasUsage.inFormula` already separates arithmetic from prose, and its own note warns off a
 * table of field paths per block type — "wrong the first time a block grew a field". This does
 * not build one. `conditionalEquation` is a single field name, declared once in
 * `VersionHeadFields` and shared by every branching block, so looking for that ONE KEY anywhere
 * in the path is a question about a universal field rather than about any block's shape. It
 * survives the field moving into a version or a scenario row, which it already has.
 *
 * ## An alias is not its member
 *
 * Deleting one block does not break a chip that names an alias with three members — the other
 * two still cover it, which is the whole point of joint coverage. So the question asked here is
 * not "does a usage mention this block" but "would this usage still resolve without it", via
 * `aliasStatus` on the alias MINUS that member. An alias that was already broken stays broken
 * and is nobody's reason to refuse a delete.
 */
/**
 * How much of the class a name answers for, worst first, so two states can be compared.
 *
 * `ok` every route, `conditional` some, `broken` none. Losing ground on this scale is what a
 * delete costs, and it is strictly more than "did it stop working altogether".
 */
const COVER = ['broken', 'conditional', 'ok'];
/** The two kinds that fail silently, and so are refused rather than reported. */
export const LOAD_BEARING = ['driven', 'branchCondition'];
export const isLoadBearing = (d) => LOAD_BEARING.includes(d.kind);
/**
 * Every dependency in the experience, grouped by the block depended ON.
 *
 * A MAP from one pass, not a function of one block id. The delete guard asks this for every
 * card on screen, and a per-block call that walked every block's data each time is quadratic in
 * an experience's blocks — on the render path, for a question whose answer is the same for all
 * of them.
 */
export function dependentsByBlock(tasks, aliases) {
    const out = new Map();
    const add = (blockId, dep) => {
        const have = out.get(blockId);
        if (have)
            have.push(dep);
        else
            out.set(blockId, [dep]);
    };
    /*
     * THE DRIVEN LINKS. A block address names its block directly, so there is nothing to
     * resolve; an ALIAS address names several, and EVERY member is load-bearing.
     *
     * Every member, not the alias as a whole, and this is the one place the two differ from the
     * recall rule below. A recall survives losing a member as long as something still covers the
     * route — the sentence still has a value. A driven axis does not: the union's rows ARE that
     * member's options, so deleting it takes a column out of the answer space and every student
     * on that route lands on Blank, silently. `aliasDriverSpace` refuses the link outright for
     * the same reason, which is the state this guard exists to keep a teacher out of.
     */
    for (const task of tasks) {
        for (const block of task.blocks ?? []) {
            const address = parseDriverAddress(block.data?.drivenBy);
            if (!address)
                continue;
            const dep = { kind: 'driven', inTaskId: task.id, inBlockId: block.id };
            if (address.kind === 'alias') {
                const alias = aliases.find(a => a.id === address.aliasId);
                for (const member of alias?.members ?? [])
                    add(member.blockId, dep);
            }
            else {
                add(address.blockId, dep);
            }
        }
    }
    /* THE RECALLS, through the alias that carries them. */
    const byId = new Map(aliases.map(a => [a.id, a]));
    for (const usage of collectAliasUsages(tasks)) {
        const alias = byId.get(usage.aliasId);
        if (!alias)
            continue;
        const kind = usage.path.includes('conditionalEquation')
            ? 'branchCondition'
            : usage.inFormula ? 'equation' : 'prose';
        for (const member of alias.members) {
            /*
             * ONLY WHERE LOSING THIS MEMBER WOULD ACTUALLY BREAK THE USAGE.
             *
             * Asked where the chip SITS, because reach is relative to the referring task and the
             * same alias can be sound on one task and not another. An alias already broken here is
             * not made worse by the delete and must not be a reason to refuse it.
             */
            /* `sameRef` directly rather than `membersWithout`, whose null means two things —
               "cannot remove the last one" and "that member is not in this alias". Here removing
               the last one is the case that matters most: it leaves nothing, which is broken. */
            const without = { ...alias, members: alias.members.filter(m => !sameRef(m, member)) };
            /*
             * DOES IT STILL COVER, not does it still RESOLVE.
             *
             * The first version asked whether the alias became `broken`, which is the same question
             * for a single member and a different one for several. Measured on two sisters used at
             * their reunion: coverage went `ok` to `conditional` and the guard reported no
             * dependents at all — so the teacher could delete one track's block and every student
             * who took that track arrived with nothing, silently, with it used in an equation AND a
             * branch condition.
             *
             * That is the owner's own case: "task y works for student 1, breaks irrevocably for
             * student 2." The members are EQUIVALENTS, so a survivor is not a replacement — it
             * answers for its own route and for nobody else's.
             *
             * An alias already broken here is still nobody's reason to refuse a delete, so the
             * `before` guard stays.
             */
            const before = COVER.indexOf(aliasStatus(alias, usage.inTaskId, tasks));
            const after = COVER.indexOf(aliasStatus(without, usage.inTaskId, tasks));
            const brokeIt = before > 0 && after < before;
            if (brokeIt)
                add(member.blockId, { kind, inTaskId: usage.inTaskId, inBlockId: usage.inBlockId });
        }
    }
    return out;
}
//# sourceMappingURL=dependents.js.map